import { Link } from 'react-router-dom'
import alternativePages from '@/data/alternativePages.json'
import { Header } from '@/components/landing/Header'
import { Footer } from '@/components/landing/Footer'
import { Helmet } from 'react-helmet-async'
import { Breadcrumb } from '@/components/Breadcrumb'
import { motion } from 'framer-motion'
import { toAbsoluteUrl } from '@/lib/seo'

const AlternativesPage = () => {
  // Extract tool names from alternativePages.json
  const alternatives = Object.keys(alternativePages).map(key => {
    // Get the tool name from the key (remove "alternative-" prefix)
    const toolName = key.replace('alternative-', '')
    // Capitalize the first letter
    const displayName = toolName.charAt(0).toUpperCase() + toolName.slice(1)
    
    return {
      slug: toolName,
      name: displayName,
      data: alternativePages[key]
    }
  })

  // Generate breadcrumbs
  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Alternatives", url: "/alternatives" }
  ]

  return (
    <div className="min-h-screen w-full mx-auto relative overflow-hidden bg-gradient-to-b from-gray-50 to-white">
      <Helmet>
        <title>{`Feedbask Alternatives ${new Date().getFullYear()} - Compare Feedbask with Other Tools`}</title>
        <meta 
          name="description" 
          content="Compare Feedbask with other feedback and product management tools. See why Feedbask is the better alternative for collecting user feedback."
        />
        <link rel="canonical" href={toAbsoluteUrl('/alternatives')} />
      </Helmet>

      {/* Background Blur */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none -z-10">
        <div className="absolute top-24 left-10 w-64 h-64 rounded-full bg-[#866BFF] opacity-30 blur-3xl"></div>
        <div className="absolute bottom-32 right-10 w-72 h-72 rounded-full bg-indigo-100 opacity-30 blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl px-4 mx-auto">
        <Header />

        {/* Breadcrumb */}
        <Breadcrumb items={breadcrumbs} />

        {/* Hero */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="py-16 md:py-24"
        >
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Looking for an alternative?
            </h1>
            <p className="text-xl text-gray-600 mb-10">
              Compare Feedbask with other popular tools to see why teams are switching to our simpler, more effective feedback solution.
            </p>
          </div>

          {/* Alternatives Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-16">
            {alternatives.map((alternative) => (
              <Link 
                key={alternative.slug}
                to={`/alternatives/${alternative.slug}`} 
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition group"
              >
                <div className="flex items-center mb-4">
                  {alternative.data.logo && (
                    <img 
                      src={alternative.data.logo} 
                      alt={`${alternative.name} logo`} 
                      className="h-10 w-auto mr-4 object-contain"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        const text = `Best alternative to ${alternative.name}`;
                        const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='400' height='100'>\n<rect width='100%' height='100%' fill='#EEF2FF'/>\n<text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='#4F46E5' font-family='Inter, ui-sans-serif' font-size='20'>${text}</text>\n</svg>`;
                        target.src = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
                      }}
                    />
                  )}
                  <h2 className="text-xl font-semibold text-gray-900">
                    {alternative.name}
                  </h2>
                </div>
                
                <p className="text-gray-600 mb-6 line-clamp-3">
                  {alternative.data.intro.substring(0, 150)}...
                </p>
                
                <div className="text-[#866BFF] font-medium group-hover:underline">
                  Compare Feedbask vs {alternative.name} →
                </div>
              </Link>
            ))}
          </div>

          {/* CTA Section */}
          <div className="bg-[#866BFF] text-white rounded-2xl shadow-lg mt-20 p-10 text-center">
            <h2 className="text-3xl font-bold mb-4">
              Ready to try the better alternative?
            </h2>
            <p className="text-lg mb-6 max-w-2xl mx-auto">
              Feedbask helps you collect user feedback, bug reports, feature requests, and more – all with a simple widget you can set up in minutes.
            </p>
            <a
              href="/auth"
              className="inline-block bg-white text-[#866BFF] font-semibold px-8 py-3 rounded-full hover:bg-gray-100 transition"
            >
              Get started for free
            </a>
          </div>
        </motion.section>

        <Footer />
      </div>
    </div>
  )
}

export default AlternativesPage 